import { useI18n } from '@/i18n'
import { useOnAuthBehaviour } from '@/stores/auth'
import { APIConsume, useRetrieveHelpers } from '@/util'
import { APIObject, APIStoreOptions, URLBuilder } from '@rokoli/bnb'
import {
  APICreate,
  APIListPaginated,
  APIRemove,
  APIRetrieve,
  APIUpdate,
  createExtendableAPI,
} from '@rokoli/bnb/drf'
import { defineStore } from 'pinia'
import { watch } from 'vue'

export function createBaseAPIStore<T extends APIObject>(
  storeId: string,
  endpoint: URLBuilder,
  options?: APIStoreOptions,
  behaviour?: { syncOnAuth?: boolean; syncOnLocaleChange?: boolean },
) {
  return defineStore(storeId, () => {
    const { api, base } = createExtendableAPI<T>(endpoint, options)
    const listOperations = APIListPaginated(api)
    const consumeOperations = APIConsume(listOperations.listIsolated)
    const retrieveOperations = APIRetrieve(api)
    const { locale } = useI18n()

    function updateCache() {
      const headers: Record<string, string> = behaviour?.syncOnLocaleChange
        ? { 'Accept-Language': `${locale.value}, *` }
        : {}
      return consumeOperations.consume({ requestInit: { headers } })
    }

    const isSynced = behaviour?.syncOnAuth
      ? useOnAuthBehaviour(updateCache).done
      : Promise.resolve(true)

    if (behaviour?.syncOnLocaleChange) {
      watch(locale, updateCache)
    }

    return {
      isSynced,
      ...base,
      ...listOperations,
      ...consumeOperations,
      ...retrieveOperations,
      ...APICreate(api),
      ...APIUpdate(api),
      ...APIRemove(api),
      ...useRetrieveHelpers({ ...base, ...retrieveOperations }),
    }
  })
}
